import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, GripVertical, X, AlertTriangle, MessageCircle, MessageSquare } from 'lucide-react';
import TrustScoreBadge from '../components/TrustScoreBadge';
import StatusPill from '../components/StatusPill';

export default function WaitlistPage() {
  const { t, i18n } = useTranslation();
  const { user, practice, activePracticeId } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const dragRef = useRef({ from: null });

  // Superadmin must pick a practice (via Standort-Switcher in topbar).
  // When they have one selected, treat them like an admin of that practice.
  const isSuperadminWithoutPractice = user?.role === 'superadmin' && !activePracticeId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isSuperadminWithoutPractice) { setPatients([]); setLoading(false); return; }
      const r = await api.get('/api/waitlist');
      setPatients(r.data.patients || []);
    } catch (e) { toast.error(t('errors.loadFailed')); }
    finally { setLoading(false); }
  }, [t, isSuperadminWithoutPractice]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const dateLocale = i18n.language?.startsWith('en') ? 'en-GB' : 'de-AT';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  function onDragStart(idx) { return (e) => { dragRef.current.from = idx; e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {} }; }
  function onDragOver(e) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }
  async function onDrop(toIdx) {
    const fromIdx = dragRef.current.from;
    dragRef.current.from = null;
    if (fromIdx == null || fromIdx === toIdx) return;
    const next = [...patients];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setPatients(next);
    try {
      await api.patch('/api/waitlist/reorder', { order: next.map((p) => p.id) });
      toast.success(t('waitlist.reorderSaved'));
    } catch (e) { toast.error(t('errors.unknown')); load(); }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/waitlist/${deleteTarget.id}`);
      toast.success(t('waitlist.delete.success'));
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(t('errors.unknown')); }
  }

  return (
    <div data-testid="waitlist-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('waitlist.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">{t('waitlist.subtitle')}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="so-btn so-btn-primary" disabled={isSuperadminWithoutPractice} data-testid="add-patient-btn">
          <Plus size={16}/> {t('waitlist.addButton')}
        </button>
      </div>

      {isSuperadminWithoutPractice && (
        <div className="so-card mt-6 p-4 flex items-start gap-3 border-warning/40 bg-warning/5" data-testid="superadmin-warning">
          <AlertTriangle size={18} className="text-warning mt-0.5" />
          <p className="text-sm text-text">Super-Admin: Bitte wählen Sie zuerst einen Standort im Standort-Switcher (oben rechts), um die Warteliste zu verwalten.</p>
        </div>
      )}

      <div className="so-card mt-6 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-text-secondary text-sm">{t('common.loading')}</div>
        ) : patients.length === 0 ? (
          <div className="p-10 text-center" data-testid="waitlist-empty">
            <p className="text-text font-medium">{t('waitlist.empty')}</p>
            <p className="mt-1 text-sm text-text-secondary">{t('waitlist.emptyHint')}</p>
          </div>
        ) : (
          <>
            <p className="px-4 pt-3 text-xs text-text-secondary">{t('waitlist.dragHint')}</p>
            <table className="so-table" data-testid="waitlist-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>{t('waitlist.table.position')}</th>
                  <th>{t('waitlist.table.name')}</th>
                  <th>{t('waitlist.table.phone')}</th>
                  <th>{t('waitlist.table.channel')}</th>
                  <th>{t('waitlist.table.trust')}</th>
                  <th>{t('waitlist.table.consent')}</th>
                  <th>{t('waitlist.table.createdAt')}</th>
                  <th>{t('waitlist.table.status')}</th>
                  <th style={{ width: 110 }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((p, idx) => (
                  <tr key={p.id} draggable onDragStart={onDragStart(idx)} onDragOver={onDragOver} onDrop={(e) => { e.preventDefault(); onDrop(idx); }} data-testid={`waitlist-row-${p.id}`} className="cursor-grab active:cursor-grabbing">
                    <td>
                      <div className="flex items-center gap-2 text-text-secondary">
                        <GripVertical size={14}/>
                        <span className="font-mono text-text">{idx + 1}</span>
                      </div>
                    </td>
                    <td className="font-medium">{p.first_name} {p.last_name}</td>
                    <td className="font-mono text-xs">{p.phone}</td>
                    <td>
                      <span className="inline-flex items-center gap-1 text-xs text-text-secondary uppercase">
                        {p.channel === 'sms' ? <MessageSquare size={12}/> : <MessageCircle size={12}/>}
                        {p.channel || 'auto'}
                      </span>
                    </td>
                    <td>
                      <TrustScoreBadge
                        score={typeof p.trust_score === 'number' ? p.trust_score : 50}
                        isNew={!p.response_history || p.response_history.length === 0}
                        label={t('waitlist.trustNew')}
                      />
                    </td>
                    <td>
                      {p.consent_given ? (
                        <span className="so-pill so-pill-success" data-testid="consent-yes">
                          {p.consent_source === 'public_form' ? t('waitlist.consentPublic') : t('waitlist.consentYes')}
                        </span>
                      ) : <span className="so-pill so-pill-danger">—</span>}
                    </td>
                    <td className="text-text-secondary">{fmtDate(p.created_at)}</td>
                    <td>
                      {p.opt_out ? <span className="so-pill so-pill-danger">{t('waitlist.optOut')}</span> : <StatusPill status={p.status} label={t(`waitlist.status.${p.status}`, p.status)}/>}
                    </td>
                    <td>
                      <button onClick={() => setDeleteTarget(p)} className="text-danger hover:underline text-sm inline-flex items-center gap-1" data-testid={`delete-patient-btn-${p.id}`}>
                        <Trash2 size={14}/> {t('waitlist.delete.button')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {showForm && <AddPatientModal onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {deleteTarget && <ConfirmDeleteModal patient={deleteTarget} onCancel={() => setDeleteTarget(null)} onConfirm={handleDelete} />}
    </div>
  );
}

function AddPatientModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', preferred_time: 'egal', channel: 'auto', note: '', consent_given: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Blocking alert when the admin tries to save without ticking the consent
  // checkbox. Patient data may NOT enter our system without DSGVO consent.
  const [showConsentAlert, setShowConsentAlert] = useState(false);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); }
  function validate() {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t('waitlist.form.errors.firstName');
    if (!form.last_name.trim()) e.last_name = t('waitlist.form.errors.lastName');
    if (!/^\+\d{8,15}$/.test(form.phone.trim())) e.phone = t('waitlist.form.errors.phone');
    setErrors(e);
    return Object.keys(e).length === 0;
  }
  async function submit(e) {
    e.preventDefault();
    // Consent gate FIRST — blocking modal, stays on form, no destructive action.
    if (!form.consent_given) { setShowConsentAlert(true); return; }
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post('/api/waitlist', {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        preferred_time: form.preferred_time,
        channel: form.channel,
        note: form.note.trim(),
        consent_given: true,
      });
      toast.success(t('waitlist.form.success'));
      onCreated();
    } catch (err) {
      const details = err.response?.data?.details;
      if (details) setErrors(Object.fromEntries(Object.entries(details).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])));
      else toast.error(err.response?.data?.message || t('errors.unknown'));
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-text/40 backdrop-blur-sm p-4 overflow-y-auto" data-testid="add-patient-modal">
      <div className="so-card w-full max-w-lg p-6 mt-12">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-bold">{t('waitlist.form.title')}</h2>
          <button onClick={onClose} className="text-text-secondary hover:text-text" aria-label="close" data-testid="modal-close-btn"><X size={18}/></button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="so-label">{t('waitlist.form.firstName')}</label>
              <input value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} className="so-input" data-testid="patient-first-name" />
              {errors.first_name && <p className="mt-1 text-xs text-danger">{errors.first_name}</p>}
            </div>
            <div>
              <label className="so-label">{t('waitlist.form.lastName')}</label>
              <input value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} className="so-input" data-testid="patient-last-name" />
              {errors.last_name && <p className="mt-1 text-xs text-danger">{errors.last_name}</p>}
            </div>
          </div>
          <div>
            <label className="so-label">{t('waitlist.form.phone')}</label>
            <input value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder={t('waitlist.form.phonePlaceholder')} className="so-input font-mono" data-testid="patient-phone" />
            {errors.phone && <p className="mt-1 text-xs text-danger">{errors.phone}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="so-label">{t('waitlist.form.preferredTime')}</label>
              <select value={form.preferred_time} onChange={(e) => setField('preferred_time', e.target.value)} className="so-input" data-testid="patient-preferred-time">
                <option value="vormittag">{t('waitlist.form.preferredTimes.vormittag')}</option>
                <option value="nachmittag">{t('waitlist.form.preferredTimes.nachmittag')}</option>
                <option value="abend">{t('waitlist.form.preferredTimes.abend')}</option>
                <option value="egal">{t('waitlist.form.preferredTimes.egal')}</option>
              </select>
            </div>
            <div>
              <label className="so-label">{t('waitlist.form.channel')}</label>
              <select value={form.channel} onChange={(e) => setField('channel', e.target.value)} className="so-input" data-testid="patient-channel">
                <option value="auto">{t('common.channelOptions.auto')}</option>
                <option value="whatsapp">{t('common.channelOptions.whatsapp')}</option>
                <option value="sms">{t('common.channelOptions.sms')}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="so-label">{t('waitlist.form.note')} <span className="lowercase text-text-secondary">({t('common.optional')})</span></label>
            <textarea value={form.note} onChange={(e) => setField('note', e.target.value)} placeholder={t('waitlist.form.notePlaceholder')} className="so-input" rows={2} data-testid="patient-note" />
          </div>
          <label className="flex items-start gap-3 rounded-md border border-border bg-background/60 p-3 cursor-pointer" data-testid="consent-label">
            <input type="checkbox" checked={form.consent_given} onChange={(e) => setField('consent_given', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border text-accent focus:ring-accent" data-testid="patient-consent-checkbox" />
            <span className="text-sm text-text">
              {t('waitlist.form.consentLabel')}
              <span className="block text-xs text-text-secondary mt-1">{t('waitlist.form.errors.consent')} (DSGVO Art. 6 Abs. 1 lit. a)</span>
            </span>
          </label>
          {errors.consent_given && <p className="text-xs text-danger" data-testid="consent-error">{errors.consent_given}</p>}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="so-btn so-btn-secondary" data-testid="modal-cancel-btn">{t('common.cancel')}</button>
            <button type="submit" className="so-btn so-btn-primary" disabled={submitting} data-testid="patient-submit-btn">
              {submitting ? t('waitlist.form.submitting') : t('waitlist.form.submit')}
            </button>
          </div>
        </form>
      </div>
      {showConsentAlert && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-text/60 backdrop-blur-sm p-4"
          onClick={() => setShowConsentAlert(false)}
          data-testid="consent-alert-backdrop"
        >
          <div
            className="so-card max-w-md w-full p-6 border-l-4 border-danger"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="consent-alert-title"
            data-testid="consent-alert-modal"
          >
            <h3 id="consent-alert-title" className="text-base font-semibold text-danger" data-testid="consent-alert-title">
              {t('waitlist.form.consentAlertTitle')}
            </h3>
            <p className="mt-3 text-sm text-text leading-relaxed" data-testid="consent-alert-body">
              {t('waitlist.form.consentAlertBody')}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowConsentAlert(false)}
                className="so-btn so-btn-primary"
                data-testid="consent-alert-ok-btn"
                autoFocus
              >
                {t('waitlist.form.consentAlertOk')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmDeleteModal({ patient, onCancel, onConfirm }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-text/40 backdrop-blur-sm p-4" data-testid="delete-modal">
      <div className="so-card w-full max-w-md p-6">
        <div className="flex gap-3">
          <div className="rounded-full bg-danger/10 p-2 text-danger h-min"><AlertTriangle size={20}/></div>
          <div className="flex-1">
            <h2 className="text-lg font-bold">{t('waitlist.delete.title')}</h2>
            <p className="mt-1 text-sm text-text">
              <span className="font-semibold">{patient.first_name} {patient.last_name}</span>
              <span className="text-text-secondary"> · {patient.phone}</span>
            </p>
            <p className="mt-3 text-sm text-text-secondary">{t('waitlist.delete.text')}</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-5">
          <button onClick={onCancel} className="so-btn so-btn-secondary" data-testid="delete-cancel-btn">{t('common.cancel')}</button>
          <button onClick={onConfirm} className="so-btn so-btn-danger" data-testid="delete-confirm-btn">
            <Trash2 size={14}/> {t('waitlist.delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
